/**
 * Shared Markdown renderer for all agent output surfaces.
 * Uses react-markdown + remark-gfm with custom code block highlighting.
 */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * @param {Object} props
 * @param {string} props.content - Markdown text to render
 * @param {'light'|'dark'} [props.theme='light'] - Color theme
 * @param {function} [props.codeHighlighter] - Optional (code, lang) => React node for syntax highlighting
 * @param {string} [props.className]
 */
export default function MarkdownRenderer({ content, theme = 'light', codeHighlighter, className = '' }) {
  const isDark = theme === 'dark';

  const components = useMemo(() => ({
    // Fenced code blocks (pre > code)
    pre({ children }) {
      return <>{children}</>;
    },
    code({ node, inline, className: codeClass, children, ...props }) {
      const lang = (codeClass || '').replace('language-', '');
      const code = String(children).replace(/\n$/, '');
      const isBlock = !inline && lang;

      if (!isBlock) {
        return (
          <code
            className={`px-1 py-0.5 rounded text-[10px] font-mono ${
              isDark ? 'bg-slate-800 text-emerald-300' : 'bg-slate-100 text-emerald-700'
            }`}
            {...props}
          >
            {code}
          </code>
        );
      }

      return (
        <div className={`my-1.5 rounded overflow-x-auto border ${
          isDark ? 'bg-[#1a1f2e] border-slate-700/50' : 'bg-slate-50 border-slate-200'
        }`}>
          {lang && (
            <div className={`px-2.5 py-0.5 text-[9px] border-b font-mono ${
              isDark ? 'text-slate-400 border-slate-700/40' : 'text-slate-500 border-slate-200'
            }`}>
              {lang}
            </div>
          )}
          <pre className={`px-2.5 py-1.5 text-[10px] font-mono leading-relaxed whitespace-pre-wrap m-0 ${
            isDark ? 'text-slate-300' : 'text-slate-800'
          }`}>
            <code>
              {codeHighlighter ? codeHighlighter(code, lang) : code}
            </code>
          </pre>
        </div>
      );
    },

    // Tables
    table({ children }) {
      return (
        <div className={`my-1.5 overflow-x-auto rounded border ${
          isDark ? 'border-slate-700/50' : 'border-slate-200'
        }`}>
          <table className="text-[10px] font-mono w-full border-collapse">
            {children}
          </table>
        </div>
      );
    },
    thead({ children }) {
      return (
        <thead className={isDark ? 'bg-slate-800/60' : 'bg-slate-100'}>
          {children}
        </thead>
      );
    },
    th({ children }) {
      return (
        <th className={`px-2 py-1 text-left font-semibold border-b ${
          isDark ? 'text-slate-300 border-slate-700/50' : 'text-slate-700 border-slate-200'
        }`}>
          {children}
        </th>
      );
    },
    td({ children }) {
      return (
        <td className={`px-2 py-0.5 border-b ${
          isDark ? 'text-slate-300 border-slate-700/30' : 'text-slate-700 border-slate-100'
        }`}>
          {children}
        </td>
      );
    },
    tr({ children, ...props }) {
      const idx = props.node?.position?.start?.line || 0;
      return (
        <tr className={idx % 2 === 0
          ? (isDark ? 'bg-[#161b22]' : 'bg-white')
          : (isDark ? 'bg-[#1a1f2e]' : 'bg-slate-50/50')
        }>
          {children}
        </tr>
      );
    },

    // Links
    a({ href, children }) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener"
          className={`hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
        >
          {children}
        </a>
      );
    },

    // Headings
    h1({ children }) { return <div className={`text-[13px] font-bold mt-2 mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</div>; },
    h2({ children }) { return <div className={`text-[12px] font-bold mt-1.5 mb-0.5 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</div>; },
    h3({ children }) { return <div className={`text-[11px] font-semibold mt-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{children}</div>; },

    // Paragraphs
    p({ children }) {
      return <p className={`my-1 leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{children}</p>;
    },

    // Lists
    ul({ children }) {
      return <ul className={`my-1 pl-4 space-y-0.5 list-disc ${isDark ? 'marker:text-slate-500' : 'marker:text-slate-400'}`}>{children}</ul>;
    },
    ol({ children }) {
      return <ol className={`my-1 pl-4 space-y-0.5 list-decimal ${isDark ? 'marker:text-slate-500' : 'marker:text-slate-400'}`}>{children}</ol>;
    },
    li({ children }) {
      return <li className={`leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{children}</li>;
    },

    // Blockquote
    blockquote({ children }) {
      return (
        <blockquote className={`my-1 pl-3 border-l-2 ${
          isDark ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-600'
        } italic`}>
          {children}
        </blockquote>
      );
    },

    // Bold / Italic
    strong({ children }) {
      return <strong className={`font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</strong>;
    },
    em({ children }) {
      return <em className={isDark ? 'text-slate-300' : 'text-slate-600'}>{children}</em>;
    },

    // Horizontal rule
    hr() {
      return <hr className={`my-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`} />;
    },
  }), [isDark, codeHighlighter]);

  if (!content) return null;

  return (
    <div className={`text-[11px] ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
